import { Box, Button, Center, Container, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract } from '@/api/generated/models';
import { useSearchContracts } from '@/api/generated/contracts/contracts';
import { ContractStatus } from '@/api/generated/models/contractStatus';
import { useListObjects } from '@/api/generated/objects/objects';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import {
  CardSection,
  fitColumnWidth,
  ListPagination,
  ListTotalCard,
  ListTotalRow,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_OBJECT_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { contractStatusLabelKey, statusConfig } from '@/features/contracts/contractDisplay';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { formatAmount, otherPartyName } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { usePageSize } from '@/utils/usePageSize';
import { useUrlNullableParam, useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

const cellStyle = { color: 'var(--color-text-secondary)' };

// Realizirano = odobreni tjedni izvještaji (isto kao detalj ugovora i Board); preostalo = vrijednost − realizirano.
const remainingValue = (contract: Contract) =>
  (contract.contractValue ?? 0) - (contract.realizedValue ?? 0);

const handlePreviewContract = (contractId?: string) => {
  if (!contractId) return;
  void openBinaryInNewTab(`/api/v1/contracts/${contractId}/pdf`);
};

export const ContractsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [objectFilter, setObjectFilter] = useUrlNullableParam('objectId');
  const [counterpartyFilter, setCounterpartyFilter] = useUrlNullableParam('counterpartyOrgId');
  const [search, setSearch] = useUrlTextParam('search');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, objectFilter, counterpartyFilter, pageSize]);

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao.
  const { data, isLoading } = useSearchContracts(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      objectId: objectFilter ?? undefined,
      counterpartyOrgId: counterpartyFilter ?? undefined
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  // Traka UKUPNO = zbroj TRENUTNE stranice (ne cijelog filtriranog skupa).
  const totalAmount = pageItems.reduce((sum, contract) => sum + (contract.contractValue ?? 0), 0);
  const totalRealized = pageItems.reduce((sum, contract) => sum + (contract.realizedValue ?? 0), 0);
  const numberColWidth = useMemo(
    () =>
      fitColumnWidth(
        pageItems.map((c) => c.contractNumber ?? c.referenceId ?? ''),
        '600 13.5px',
        60,
        180
      ),
    [pageItems]
  );
  const desktopColumns: [string, number, ('right' | 'center' | undefined)?][] = [
    ['contracts.colPartner', 190],
    ['contracts.colObject', TABLE_OBJECT_COL_WIDTH],
    ['contracts.colNumber', numberColWidth],
    ['contracts.colValue', 110, 'right'],
    ['contracts.colRealized', 120, 'right'],
    ['contracts.colRemaining', 120, 'right'],
    ['contracts.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
  ];
  const tableMinWidthPx = tableMinWidth(
    [...desktopColumns.map(([, width]) => width), TABLE_ACTION_COL_WIDTH],
    20
  );
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const statusOptions = [
    { value: 'ALL', label: t('contracts.allStatuses') },
    ...Object.values(ContractStatus).map((status) => ({
      value: status,
      label: t(statusConfig[status].labelKey)
    }))
  ];

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive)
        .map((object) => ({
          value: object.id ?? '',
          label: formatObjectLabel(object)
        }))
        .filter((option) => option.value),
    [objectsData, t]
  );

  const { data: connectionsData } = useListOrganisationConnections();
  const counterpartyOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <Group justify="space-between" wrap="nowrap" visibleFrom="sm">
          <PageHeading title={t('contracts.title')} subtitle={t('contracts.subtitle')} />
          <Button onClick={() => navigate('/contracts/new')}>{t('contracts.create')}</Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('contracts.title')} subtitle={t('contracts.subtitle')} />
          <MobileFab onClick={() => navigate('/contracts/new')}>{t('contracts.create')}</MobileFab>
        </Stack>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('contracts.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'counterparty',
                label: t('contracts.filterCounterparty'),
                options: counterpartyOptions,
                value: counterpartyFilter,
                onChange: setCounterpartyFilter
              },
              {
                key: 'object',
                label: t('contracts.filterObject'),
                options: objectOptions,
                value: objectFilter,
                onChange: setObjectFilter
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('contracts.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <div style={{ width: 220 }}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </div>
          <div style={{ width: 200 }}>
            <SelectField
              hideLabel
              placeholder={t('contracts.filterCounterparty')}
              data={counterpartyOptions}
              value={counterpartyFilter}
              onChange={setCounterpartyFilter}
              clearable
            />
          </div>
          <div style={{ width: 200 }}>
            <SelectField
              hideLabel
              placeholder={t('contracts.filterObject')}
              data={objectOptions}
              value={objectFilter}
              onChange={setObjectFilter}
              clearable
            />
          </div>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('contracts.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: tableMinWidthPx }}>
                    <div
                      className="table-header-row"
                      style={{
                        ...tableRowStyle,
                        padding: '12px 20px',
                        backgroundColor: 'var(--color-bg-canvas)',
                        borderBottom: '1px solid var(--color-border-soft)'
                      }}
                    >
                      {desktopColumns.map(([key, width, align]) => (
                        <Text
                          key={key}
                          fz={10}
                          fw={600}
                          w={width}
                          ta={align}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                        >
                          {t(key)}
                        </Text>
                      ))}
                      <Text
                        fz={10}
                        fw={600}
                        w={TABLE_ACTION_COL_WIDTH}
                        style={{
                          ...tableCenteredCellStyle,
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4
                        }}
                      >
                        {t('contracts.colAction')}
                      </Text>
                    </div>

                    {pageItems.map((contract, index) => {
                      const config = statusConfig[contract.status ?? ContractStatus.DRAFT];

                      return (
                        <div
                          key={contract.id}
                          style={{
                            ...tableRowStyle,
                            padding: '14px 20px',
                            borderTop:
                              index === 0 ? undefined : '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text fz={13} w={190} style={cellStyle} truncate>
                            {otherPartyName(contract, user?.orgName)}
                          </Text>
                          <Tooltip
                            label={
                              contract.objectCode || contract.objectName
                                ? `${contract.objectCode ?? ''}${contract.objectCode && contract.objectName ? ' - ' : ''}${contract.objectName ?? ''}`
                                : '—'
                            }
                            openDelay={300}
                            withArrow
                            color="var(--color-bg-surface)"
                            styles={{
                              tooltip: {
                                color: 'var(--color-text-primary)',
                                border: '1px solid var(--color-border-soft)',
                                borderRadius: 8,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                              }
                            }}
                          >
                            <Text
                              fz={13}
                              w={TABLE_OBJECT_COL_WIDTH}
                              style={{
                                ...cellStyle,
                                cursor: contract.objectId ? 'pointer' : undefined
                              }}
                              truncate
                              data-own-tooltip
                              role={contract.objectId ? 'button' : undefined}
                              tabIndex={contract.objectId ? 0 : undefined}
                              onClick={() =>
                                contract.objectId &&
                                navigate(`/templates/object-registry/${contract.objectId}`)
                              }
                              onKeyDown={(event) => {
                                if (
                                  (event.key === 'Enter' || event.key === ' ') &&
                                  contract.objectId
                                )
                                  navigate(`/templates/object-registry/${contract.objectId}`);
                              }}
                            >
                              {contract.objectName ?? '—'}
                            </Text>
                          </Tooltip>
                          <Text
                            fz={13.5}
                            fw={600}
                            w={numberColWidth}
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {contract.contractNumber ?? contract.referenceId}
                          </Text>
                          <Text
                            fz={13.5}
                            fw={600}
                            w={110}
                            ta="right"
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {formatAmount(contract.contractValue)}
                          </Text>
                          <Text fz={13} w={120} ta="right" style={cellStyle}>
                            {formatAmount(contract.realizedValue)}
                          </Text>
                          <Text fz={13} w={120} ta="right" style={cellStyle}>
                            {formatAmount(remainingValue(contract))}
                          </Text>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                            <StatusPill
                              label={t(contractStatusLabelKey(contract, user?.organisationId))}
                              bg={config.bg}
                              fg={config.fg}
                            />
                          </div>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_ACTION_COL_WIDTH }}>
                            <RowActionsMenu
                              detailsHref={`/contracts/${contract.id}`}
                              historyHref={`/contracts/${contract.id}/history`}
                              onPreview={() => handlePreviewContract(contract.id)}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {pageItems.length > 0 && (
                      <ListTotalRow
                        columnWidths={desktopColumns.map(([, width]) => width)}
                        amounts={{
                          3: totalAmount,
                          4: totalRealized,
                          5: totalAmount - totalRealized
                        }}
                        actionColumnWidth={TABLE_ACTION_COL_WIDTH}
                      />
                    )}
                  </div>
                </div>

                <ListPagination
                  label={t('contracts.showing', {
                    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                    to: Math.min(page * pageSize, totalElements),
                    total: totalElements
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('contracts.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((contract) => {
                const config = statusConfig[contract.status ?? ContractStatus.DRAFT];

                return (
                  <Stack
                    key={contract.id}
                    gap={10}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-soft)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text fz={14.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                          {otherPartyName(contract, user?.orgName)}
                        </Text>
                        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                          {contract.contractNumber ?? contract.referenceId}
                          {contract.objectName && (
                            <>
                              {' · '}
                              <Text
                                span
                                inherit
                                role="button"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  navigate(`/templates/object-registry/${contract.objectId}`)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ')
                                    navigate(`/templates/object-registry/${contract.objectId}`);
                                }}
                              >
                                {contract.objectName}
                              </Text>
                            </>
                          )}
                        </Text>
                      </Stack>
                      <RowActionsMenu
                        detailsHref={`/contracts/${contract.id}`}
                        historyHref={`/contracts/${contract.id}/history`}
                        onPreview={() => handlePreviewContract(contract.id)}
                      />
                    </Group>

                    <Text fz={16} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                      {formatAmount(contract.contractValue)}
                    </Text>

                    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                      {t('contracts.realizedPrefix')} {formatAmount(contract.realizedValue)} |{' '}
                      {t('contracts.remainingPrefix')} {formatAmount(remainingValue(contract))}
                    </Text>

                    <StatusPill
                      label={t(contractStatusLabelKey(contract, user?.organisationId))}
                      bg={config.bg}
                      fg={config.fg}
                    />
                  </Stack>
                );
              })}

              {pageItems.length > 0 && (
                <ListTotalCard
                  amount={totalAmount}
                  details={[
                    { label: t('contracts.realizedPrefix'), amount: totalRealized },
                    {
                      label: t('contracts.remainingPrefix'),
                      amount: totalAmount - totalRealized
                    }
                  ]}
                />
              )}

              <ListPagination
                label={t('contracts.showing', {
                  from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalElements),
                  total: totalElements
                })}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
